import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Clock, Plus, Trash2, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  todo: { label: "To do", tone: "border-white/15 text-zinc-300 bg-white/[0.02]", icon: Clock },
  in_progress: { label: "In progress", tone: "border-indigo-500/40 text-indigo-300 bg-indigo-500/[0.06]", icon: Clock },
  blocked: { label: "Blocked", tone: "border-amber-500/40 text-amber-300 bg-amber-500/[0.06]", icon: AlertCircle },
  done: { label: "Done", tone: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]", icon: CheckCircle2 },
};

const empty = { title: "", description: "", assignee_id: "", due_date: "", status: "todo", priority: "normal" };

/**
 * CommitteeTasksTab — expanded task board for one committee. Rendered inside
 * the CommitteesPage expand drawer beneath meetings.
 */
export function CommitteeTasksTab({ committee }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/committees/${committee.id}/tasks`);
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load tasks"); }
  }, [committee.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.assignee_id) delete payload.assignee_id;
      if (!payload.due_date) delete payload.due_date;
      await api.post(`/admin/committees/${committee.id}/tasks`, payload);
      toast.success("Task added");
      setDialogOpen(false); setForm(empty);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const changeStatus = async (t, status) => {
    try {
      await api.patch(`/admin/committees/${committee.id}/tasks/${t.id}`, { status });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Update failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/committees/${committee.id}/tasks/${t.id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const remindChair = async () => {
    try {
      const { data } = await api.post(`/admin/committees/${committee.id}/tasks/remind-chair`);
      if (data.ok) {
        toast.success(`Digest sent to ${data.recipient} · ${data.overdue} overdue, ${data.upcoming} upcoming`);
      } else {
        toast.warning("No chair or admin assigned — assign one first");
      }
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to send"); }
  };

  const overdueCount = items.filter((t) =>
    t.status !== "done" && t.due_date && new Date(t.due_date) < new Date(),
  ).length;

  return (
    <div className="mt-3 border-t border-white/5 pt-3" data-testid={`committee-tasks-${committee.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">Task board</div>
        <div className="flex items-center gap-2">
          {overdueCount > 0 && <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]">{overdueCount} overdue</Badge>}
          <Button size="sm" variant="ghost" onClick={remindChair}
                  className="h-7 text-indigo-300 hover:text-indigo-200" data-testid={`committee-task-remind-${committee.id}`}>
            <Send className="h-3 w-3 mr-1" /> Email chair digest
          </Button>
          <Button size="sm" onClick={() => setDialogOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full h-7"
                  data-testid={`committee-task-add-${committee.id}`}>
            <Plus className="h-3 w-3 mr-1" /> Add task
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">No tasks yet.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((t) => {
            const s = STATUS_META[t.status] || STATUS_META.todo;
            const Icon = s.icon;
            const isOverdue = t.status !== "done" && t.due_date && new Date(t.due_date) < new Date();
            return (
              <div key={t.id} className={"rounded-md border p-2.5 " + s.tone} data-testid={`committee-task-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 shrink-0" />
                      <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                      {isOverdue && <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]">Overdue</Badge>}
                      {t.priority === "high" && <Badge variant="outline" className="border-rose-500/40 text-rose-300 text-[10px]">High</Badge>}
                    </div>
                    {t.description && <div className="text-[11px] text-zinc-500 mt-0.5 whitespace-pre-wrap">{t.description}</div>}
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {t.assignee_name || "Unassigned"}
                      {t.due_date && <span> · due {new Date(t.due_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                      <SelectTrigger className="h-7 w-28 bg-white/[0.04] border-white/10 text-[11px]"
                                      data-testid={`committee-task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                        {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => remove(t)} className="text-zinc-500 hover:text-red-300 p-1" data-testid={`committee-task-delete-${t.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[440px]" data-testid={`committee-task-dialog-${committee.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-orange-400" /> New committee task</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">{committee.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Follow up on pool quote" className="bg-white/[0.04] border-white/10" data-testid="task-title" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description (optional)</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="bg-white/[0.04] border-white/10 text-sm" data-testid="task-description" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Assignee</Label>
                <Select value={form.assignee_id || "__none__"}
                        onValueChange={(v) => setForm({ ...form, assignee_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    <SelectItem value="__none__">— Unassigned —</SelectItem>
                    {(committee.members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    {committee.chair && <SelectItem value={committee.chair.id}>{committee.chair.name} (chair)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="task-due" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.title}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="task-save-btn">
              {busy ? "Saving…" : "Add task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
